import React from "react";
import { AlertTriangle, X } from "lucide-react";
import Button from "./Button";

const ConfirmationModal = ({
  isLoading = false,
  isOpen,
  onClose,
  onConfirm,
  title = "Confirm Action",
  message = "Are you sure you want to proceed?",
  confirmText = "Confirm",
  cancelText = "Cancel",
  variant = "danger", // 'danger', 'warning', 'info'
  icon = AlertTriangle,
}) => {
  if (!isOpen) return null;

  const Icon = icon;
  const hasIcon = Boolean(Icon);

  const getVariantStyles = () => {
    switch (variant) {
      case "danger":
        return {
          iconColor: "text-red-500",
          iconBg: "bg-red-50",
          buttonVariant: "danger",
        };
      case "warning":
        return {
          iconColor: "text-yellow-500",
          iconBg: "bg-yellow-50",
          buttonVariant: "warning",
        };
      case "info":
        return {
          iconColor: "text-blue-500",
          iconBg: "bg-blue-50",
          buttonVariant: "primary",
        };
      default:
        return {
          iconColor: "text-gray-500",
          iconBg: "bg-gray-50",
          buttonVariant: "primary",
        };
    }
  };

  const styles = getVariantStyles();
  const isDangerLike = variant === "danger";

  const handleConfirm = () => {
    onConfirm();
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto p-4">
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-[#1B3F7A]/45 backdrop-blur-[2px] transition-opacity"
        onClick={onClose}
        aria-hidden
      />

      {/* Modal — centered on all viewports */}
      <div className="relative z-10 w-full max-w-md overflow-hidden rounded-[28px] border border-[#D7E4FB] bg-[#F7FBFF] text-left shadow-[0_24px_80px_rgba(35,74,140,0.22)]">
        {/* Header */}
        <div className={`flex items-start justify-between px-6 ${hasIcon ? "pt-6" : "pt-8"}`}>
          <div className="flex flex-1 items-start justify-center">
            <div className="w-full max-w-[320px] text-center">
              {hasIcon && (
                <div
                  className={`mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full border ${
                    isDangerLike
                      ? "border-[#D8E6FF] bg-[#EAF2FF]"
                      : styles.iconBg
                  }`}
                >
                  <Icon size={24} className={styles.iconColor} />
                </div>
              )}
              <h3 className="text-[22px] font-semibold leading-tight text-[#274177]">
                {title}
              </h3>
            </div>
          </div>
          <button
            onClick={onClose}
            className="absolute right-4 top-4 rounded-full p-1.5 text-[#7B93BB] transition-colors hover:bg-[#EAF2FF] hover:text-[#274177]"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Content */}
        <div className={`px-6 pb-6 ${hasIcon ? "pt-3" : "pt-4"}`}>
          <p className="mx-auto max-w-[320px] text-center text-[16px] leading-6 text-[#5D7198]">
            {message}
          </p>
        </div>

        {/* Footer */}
        <div className="px-6 pb-6">
          <div className="space-y-3">
            <Button
              loading={isLoading}
              variant={isDangerLike ? "primary" : styles.buttonVariant}
              onClick={handleConfirm}
              size="lg"
              className="h-14 w-full !rounded-full bg-[#2E63B8] text-[16px] font-semibold shadow-[0_10px_24px_rgba(46,99,184,0.22)] hover:bg-[#2858A4]"
            >
              {confirmText}
            </Button>
            <button
              type="button"
              onClick={onClose}
              className="h-14 w-full rounded-full border border-[#BFD0EE] bg-white text-[16px] font-semibold text-[#274177] transition-colors hover:bg-[#F2F7FF]"
            >
              {cancelText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;
